import { useMemo, useState } from 'react';
import Button from '../../../shared/components/Button';
import { useAuthStore } from '../../../features/auth/store';
import { useTenantStore } from '../../../shared/stores/useTenantStore';
import { useNoResultSearches, useSearchStats, useTopSearches } from '../hooks/useSearchAnalytics';
import type { SearchAnalyticsPeriod } from '../types';

const PERIODS: { value: SearchAnalyticsPeriod; label: string }[] = [
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const fmtCount = new Intl.NumberFormat('en-US');
const fmtDate = (iso: string) => new Date(iso).toLocaleString();

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-5">
      <p className="text-[13px] text-muted-foreground">{label}</p>
      <p className="mt-2 text-[24px] font-semibold text-foreground tabular-nums">{value}</p>
      {sub && <p className="mt-1 text-[12px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function SearchTermTable({
  title,
  description,
  rows,
  isLoading,
  emptyMessage,
}: {
  title: string;
  description: string;
  rows: { query: string; count: number; score: number; lastSearchedAt: string }[];
  isLoading: boolean;
  emptyMessage: string;
}) {
  return (
    <section className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-[15px] font-semibold text-foreground">{title}</h2>
        <p className="text-[13px] text-muted-foreground mt-1">{description}</p>
      </div>
      {isLoading ? (
        <div className="px-6 py-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-muted-foreground">{emptyMessage}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px]">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Search term</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Volume</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Last searched</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((row) => (
                <tr key={row.query} className="hover:bg-muted transition-colors">
                  <td className="px-6 py-4 text-sm text-foreground">{row.query}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground tabular-nums">
                    {fmtCount.format(row.count)}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {fmtDate(row.lastSearchedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * TCS-admin search analytics (PE-1288 FE; backend already shipped — osprey-api
 * `search-analytics.controller.ts` proxying analytics-service). Merchant-scoped:
 * the backend 400s without an `x-tenant-id` header, so this page is inert until
 * a merchant is selected via the sidebar's MerchantPicker (same convention as
 * Homepage Slider / Portfolio).
 */
export function SearchAnalyticsPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const openSelector = useTenantStore((s) => s.openSelector);

  const [period, setPeriod] = useState<SearchAnalyticsPeriod>('7d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState(10);

  const useCustomRange = !!(from && to);
  const rangeInvalid = useCustomRange && new Date(from) > new Date(to);

  const baseQuery = useCustomRange && !rangeInvalid ? { from, to } : { period };
  const hasMerchant = !!selectedTenantId;

  const stats = useSearchStats(baseQuery, hasMerchant && !rangeInvalid);
  const topSearches = useTopSearches({ ...baseQuery, limit }, hasMerchant && !rangeInvalid);
  const noResultSearches = useNoResultSearches({ ...baseQuery, limit }, hasMerchant && !rangeInvalid);

  const isLoading = stats.isLoading || topSearches.isLoading || noResultSearches.isLoading;

  const noResultRateLabel = useMemo(() => {
    if (!stats.data) return undefined;
    return `${fmtCount.format(stats.data.noResultSearches)} of ${fmtCount.format(stats.data.totalSearches)} searches`;
  }, [stats.data]);

  return (
    <div className="py-6 max-w-7xl mx-auto px-2">
      <div className="mb-6">
        <h1 className="text-[23px] font-bold text-foreground">Search analytics</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          What buyers search for on this merchant&apos;s storefront — volume, top terms and
          zero-result (&quot;dead-end&quot;) searches.
        </p>
      </div>

      {!hasMerchant ? (
        <div className="bg-card rounded-2xl border border-border py-16 text-center">
          <p className="text-[14px] text-muted-foreground">
            Select a merchant to view its search analytics.
          </p>
          <Button className="mt-4 h-[40px]" onClick={openSelector}>
            Select merchant
          </Button>
        </div>
      ) : (
        <>
          {/* ---- Filters ---- */}
          <div className="bg-card rounded-2xl border border-border p-5 mb-6">
            <div className="flex flex-wrap items-end gap-4">
              <label className="block">
                <span className="text-[13px] font-medium text-foreground">Period</span>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as SearchAnalyticsPeriod)}
                  disabled={useCustomRange}
                  className="mt-1 h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm disabled:opacity-50"
                >
                  {PERIODS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[13px] font-medium text-foreground">From</span>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="mt-1 h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-[13px] font-medium text-foreground">To</span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="mt-1 h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-[13px] font-medium text-foreground">Top N</span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={limit}
                  onChange={(e) => setLimit(Math.min(50, Math.max(1, Number(e.target.value) || 10)))}
                  className="mt-1 h-[40px] w-[80px] rounded-[8px] border border-border bg-background px-3 text-sm"
                />
              </label>
              {useCustomRange && (
                <Button variant="outline" className="h-[40px]" onClick={() => { setFrom(''); setTo(''); }}>
                  Clear dates
                </Button>
              )}
              {rangeInvalid && (
                <span className="text-[12px] text-red-500">&quot;From&quot; must be on or before &quot;To&quot;.</span>
              )}
            </div>
            <p className="mt-3 text-[12px] text-muted-foreground">
              A custom date range overrides the period. Both bounds are inclusive.
            </p>
          </div>

          {/* ---- Headline tiles ---- */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <StatTile
              label="Total searches"
              value={stats.data ? fmtCount.format(stats.data.totalSearches) : '—'}
            />
            <StatTile
              label="Unique search terms"
              value={stats.data ? fmtCount.format(stats.data.uniqueQueries) : '—'}
              sub={stats.data ? `${stats.data.avgSearchesPerQuery.toFixed(1)} searches per term, on average` : undefined}
            />
            <StatTile
              label="Search success rate"
              value={stats.data ? `${stats.data.searchSuccessRate.toFixed(1)}%` : '—'}
            />
            <StatTile
              label="No-result rate"
              value={stats.data ? `${stats.data.noResultRate.toFixed(1)}%` : '—'}
              sub={noResultRateLabel}
            />
          </div>

          {stats.error && (
            <div className="mb-6 text-center py-6 text-red-500 text-sm">
              Failed to load search analytics for this merchant.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SearchTermTable
              title="Top searches"
              description="The most frequent search terms in the selected window."
              rows={topSearches.data?.data ?? []}
              isLoading={isLoading}
              emptyMessage="No searches recorded in this window."
            />
            <SearchTermTable
              title="No-result searches"
              description="Search terms that returned zero results — likely catalogue or synonym gaps."
              rows={noResultSearches.data?.data ?? []}
              isLoading={isLoading}
              emptyMessage="No zero-result searches in this window."
            />
          </div>
        </>
      )}
    </div>
  );
}
